import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  Divider,
  Button,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  NotificationsNone as EmptyIcon,
  DoneAll as DoneAllIcon,
  EmojiEvents as WinIcon,
  Dashboard as BoardIcon,
  Campaign as SystemIcon,
} from '@mui/icons-material';
import { useNotifications } from '../hooks';
import { NotificationItem } from '../components/shared/NotificationItem';
import { PageHeader } from '../components/shared/PageHeader';
import { NotificationCategory } from '../api';

export const NotificationsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    category,
    setCategory,
  } = useNotifications();

  const handleCategoryChange = (
    _event: React.MouseEvent<HTMLElement>,
    newCategory: NotificationCategory | null
  ) => {
    if (newCategory !== null) {
      setCategory(newCategory);
    }
  };

  const getCategoryIcon = (cat: NotificationCategory) => {
    switch (cat) {
      case 'wins':
        return <WinIcon sx={{ fontSize: 18 }} />;
      case 'board_updates':
        return <BoardIcon sx={{ fontSize: 18 }} />;
      case 'system':
        return <SystemIcon sx={{ fontSize: 18 }} />;
      default:
        return null;
    }
  };

  return (
    <Container maxWidth="md">
      <PageHeader
        title="Notifications"
        subtitle={unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
        actions={
          unreadCount > 0 ? (
            <Button
              variant="outlined"
              startIcon={<DoneAllIcon />}
              onClick={markAllAsRead}
              size={isMobile ? 'small' : 'medium'}
            >
              Mark all read
            </Button>
          ) : undefined
        }
      />

      {/* Category filter tabs */}
      <Paper sx={{ mb: 2, p: 1 }}>
        <ToggleButtonGroup
          value={category}
          exclusive
          onChange={handleCategoryChange}
          aria-label="notification category filter"
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? 0.5 : 0,
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              px: isMobile ? 1.5 : 2,
              py: isMobile ? 0.75 : 1,
              flex: isMobile ? '1 1 45%' : 'initial',
            },
          }}
        >
          <ToggleButton value="all">
            All
          </ToggleButton>
          <ToggleButton value="wins">
            {getCategoryIcon('wins')}
            <Box component="span" sx={{ ml: 0.5 }}>Wins</Box>
          </ToggleButton>
          <ToggleButton value="board_updates">
            {getCategoryIcon('board_updates')}
            <Box component="span" sx={{ ml: 0.5 }}>Boards</Box>
          </ToggleButton>
          <ToggleButton value="system">
            {getCategoryIcon('system')}
            <Box component="span" sx={{ ml: 0.5 }}>System</Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Paper>

      {/* Notifications list */}
      <Paper>
        {error && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button
              onClick={() => window.location.reload()}
              sx={{ mt: 1 }}
            >
              Retry
            </Button>
          </Box>
        )}

        {isLoading && notifications.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No notifications
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {category === 'all'
                ? "You're all caught up! Notifications about wins, board updates, and announcements will appear here."
                : `No ${category.replace('_', ' ')} notifications yet.`}
            </Typography>
          </Box>
        ) : (
          <>
            <List disablePadding>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  <NotificationItem
                    notification={notification}
                    onMarkAsRead={markAsRead}
                  />
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            {/* Load more */}
            {hasMore && (
              <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                <Button
                  onClick={loadMore}
                  disabled={isLoading}
                  sx={{ textTransform: 'none' }}
                >
                  {isLoading ? (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  ) : null}
                  Load more notifications
                </Button>
              </Box>
            )}

            {!hasMore && notifications.length > 0 && (
              <Box sx={{ p: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                  You've reached the end
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Summary stats */}
      {notifications.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`${notifications.filter(n => !n.isRead).length} unread`}
            color="primary"
            variant="outlined"
          />
          <Chip
            size="small"
            label={`${notifications.length} total`}
            variant="outlined"
          />
        </Box>
      )}
    </Container>
  );
};
